import {dateToServer, endpoint} from 'services/Api';
import {RESOURCE as PARENT} from 'services/api/calculators';
import {extractIncome} from 'services/personFunctions';

/**
 * Resource Endpoint.
 *
 * @type {String}
 */
const RESOURCE = 'monetary';

function extractNetIncomeForServer(person) {
    const net_income = extractIncome(person, 'net');
    return {
        net_income
    };
}

const extractSinglePeriod = (income, period) => {
    // Retired at death, that'll do
    if (income.retired) {
        return [{
            is_pension: true,
            employment_type: 'retired',
            start_of_period: dateToServer(period.startOfPeriod),
            end_of_period: dateToServer(period.endOfPeriod),
            net_income: period.netPensionIncome,
        }];
    }
    return [
        // Normal period
        {
            employment_type: period.employmentType,
            start_of_period: dateToServer(period.startOfPeriod),
            end_of_period: dateToServer(income.retirementDate),
            net_income: period.netIncome,
        },
        // Pension period
        {
            is_pension: true,
            employment_type: 'retired',
            start_of_period: dateToServer(income.retirementDate),
            end_of_period: dateToServer(period.endOfPeriod),
            net_income: period.netPensionIncome,
        },
    ];
};

const extractMultiplePeriods = (income, periods) => {
    const ret = [];
    periods.forEach((period) => {
        if (period.netPensionIncome > 0) {
            ret.push({
                is_pension: true,
                employment_type: 'retired',
                start_of_period: dateToServer(period.startOfPeriod),
                end_of_period: dateToServer(period.endOfPeriod),
                net_income: period.netPensionIncome,
            });
        }
        // Normal period
        if (period.netIncome > 0) {
            ret.push({
                employment_type: period.employmentType,
                start_of_period: dateToServer(period.startOfPeriod),
                end_of_period: dateToServer(period.endOfPeriod),
                net_income: period.netIncome,
            });
        }
    });

    return ret;
};

const extractForIncomeTable = (start) => (person) => {
    const income = person.income;
    const periods = income.periods;
    if (!periods[0]) {
        return null;
    }
    const serverPeriods = income.unchangedPension ?
        extractSinglePeriod(income, periods[0]) :
        extractMultiplePeriods(income, periods);

    return {
        name: person.name,
        gender: person.gender,
        date_of_birth: dateToServer(person.dateOfBirth),
        date_of_death: dateToServer(person.dateOfDeath),
        date_of_death_assumed: dateToServer(person.lifeExpectancy.dateOfDeath),
        income: serverPeriods,
    };
};

/**
 * Monetary Calculator Endpoints:
 *     - Pension Loss Endpoint
 *     - Income
 *     - Multiplicand
 *     - Multipliers
 *     - Multiplier TableData (1 & 2 and 28)
 *     - Services Multiplier
 *     - Services TableData
 */
export default {
    /**
     * Pension Loss Endpoint.
     *
     * @param  {} pension
     *
     * @return {}
     */
    pensionLoss(claimant, pension) {
        let data = {
            claimant,
            pension,
        };

        return endpoint(`${PARENT}/${RESOURCE}`, 'pension-loss', data);
    },
    /**
     * Multiplicand Endpoint.
     *
     * @param  {} deceasedIncome
     * @param  {} dependantsIncome
     * @param  {} percentage
     *
     * @return {}
     */
    futureLoss(duration, discountRate) {
        let data = {
            duration,
            discountRate,
        };

        return endpoint(`${PARENT}/${RESOURCE}`, 'future-loss', data).then(data => parseFloat(data.value));
    },
    /**
     * Income Endpoint.
     *
     * @param  {} income
     * @param  {} person
     *
     * @return {object}
     */
    income(income, person) {
        return endpoint(`${PARENT}/${RESOURCE}`, 'income', {
            employment_income: income.employmentIncome,
            employment_net_gross: income.employmentNetGross,
            employment_type: income.employmentType,
            end_of_period: income.endOfPeriod,
            pension_income: income.pensionIncome,
            pension_net_gross: income.pensionNetGross,
            start_of_period: income.startOfPeriod,
            retirement_date: person.income.retirementDate,
        }).then(function (data) {
            return {
                totalGrossIncome: data.total_gross_income,
                totalNetIncome: data.total_net_income,
            };
        });
    },
    /**
     * Multiplicand Endpoint.
     *
     * @param  {} deceasedIncome
     * @param  {} dependantsIncome
     * @param  {} percentage
     *
     * @return {}
     */
    multiplicand(deceasedIncome, dependantsIncome, percentage) {
        const data = {
            deceased: deceasedIncome,
            dependants: dependantsIncome,
            percentage,
        };
        return endpoint(`${PARENT}/${RESOURCE}`, 'multiplicand', data).then(data => parseFloat(data.value));
    },
    /**
     * Dependency on Income Multiplier Endpoint.
     *
     * @param  {} person
     * @param  {} claimantName
     * @param  {} dateOfassumedSettlement
     * @param  {} type
     * @param  {} discountRate
     * @param  {} discountTable
     * @param  {} multiplicandTotal
     * @param  {} durationOfDependency
     *
     * @return {Promise}
     */
    multiplier(person, dependants, claimantName, dateOfassumedSettlement, type, discountRate, discountTable, multiplicandTotal) {
        dependants.map(dependant => dependant.dateOfDeathAssumed = dependant.lifeExpectancy.dateOfDeath);

        return endpoint(`${PARENT}/${RESOURCE}`, 'financial/multiplier', {
            person: {
                name: person.name,
                gender: person.gender,
                date_of_birth: dateToServer(person.dateOfBirth),
                date_of_death: dateToServer(person.dateOfDeath),
                date_of_death_assumed: dateToServer(person.lifeExpectancy.dateOfDeath),
            },
            claim: {
                claimant_name: claimantName,
                assumed_settlement: dateToServer(dateOfassumedSettlement),
            },
            discount_rate: discountRate,
            discount_table: discountTable,
            multiplier: {
                dependency_type: type,
                value: multiplicandTotal,
            },
            loss_of_earnings: person.income.multiplier,
            dependants: (dependants) ? dependants : []
        });
    },
    /**
     * Dependency on Income Multipliers TableData
     * @param  {} theDeceased
     * @param  {} dependants
     * @param  {} claimantName
     * @param  {} assumedSettlement
     * @param  {} discountRate
     * @param  {} discountTable
     * @param  {} multiTotal
     * @param  {} durationOfDependency
     *
     * @return {Promise}
     */
    multiplierTable(theDeceased, dependants, claimantName, assumedSettlement, discountRate, discountTable, multiTotal) {
        const action = (discountTable === 1) ?
            'financial/table-1-and-2' :
            'financial/table-28';

        theDeceased.dateOfDeathAssumed = theDeceased.lifeExpectancy.dateOfDeath;
        dependants.map(dependant => dependant.dateOfDeathAssumed = dependant.lifeExpectancy.dateOfDeath);

        return endpoint(`${PARENT}/${RESOURCE}`, action, {
            deceased: theDeceased,
            dependants: dependants,
            claim: {
                claimant_name: claimantName,
                assumed_settlement: dateToServer(assumedSettlement),
            },
            discount_rate: discountRate,
            discount_table: discountTable,
            multiplier: {
                value: multiTotal,
            },
            loss_of_earnings: theDeceased.income.multiplier,
        });
    },
    /**
     * RPI Table.
     *
     * @return {Promise}
     */
    rpiTable() {
        return endpoint(`${PARENT}/${RESOURCE}`, 'rpi/load-all');
    },
    /**
     * RPI Current.
     *
     * @return {Promise}
     */
    rpiCurrent() {
        return endpoint(`${PARENT}/${RESOURCE}`, 'rpi/current');
    },
    /**
     * PSLA Tariff Table.
     *
     * @return {Promise}
     */
    pslaTable() {
        return endpoint(`${PARENT}/${RESOURCE}`, 'psla/table', null, false);
    },
    /**
     * PSLA Awards.
     *
     * @return {Promise}
     */
    pslaAwards() {
        return endpoint(`${PARENT}/${RESOURCE}`, 'psla/awards');
    },
    /**
     * Services Multiplier Calculation.
     *
     * @param  {} theDeceased
     * @param  {} claimantName
     * @param  {} issueOfProceedings
     * @param  {} services
     * @param  {} preTrial
     * @param  {} discountRate
     *
     * @return {Promise}
     */
    servicesMultiplier(theDeceased, claimantName, issueOfProceedings, services, preTrial, discountRate) {
        return endpoint(`${PARENT}/${RESOURCE}`, 'services/multiplier', {
            person: {
                name: theDeceased.name,
                gender: theDeceased.gender.toLowerCase(),
                date_of_birth: dateToServer(theDeceased.dateOfBirth),
                date_of_death: dateToServer(theDeceased.dateOfDeath),
            },
            claim: {
                claimant_name: claimantName,
                assumed_settlement: dateToServer(issueOfProceedings),
            },
            discount_rate: discountRate,
            multipliers: services.map(service => ({
                discount_table: service.discountTable,
                value: service.multiplicand || 0,
                period_of_dependency: parseFloat(service.duration) - parseFloat(preTrial),
            })),
            loss_of_earnings: theDeceased.income.multiplier
        });
    },
    /**
     * Services Multiplier TableData.
     *
     * @param  {} theDeceased
     * @param  {} claimantName
     * @param  {} issueOfProceedings
     * @param  {} services
     * @param  {} preTrial
     * @param  {} discountRate
     * @param  {} discountTable
     *
     * @return {Promise}
     */
    servicesTable(theDeceased, claimantName, issueOfProceedings, services, preTrial, discountRate, discountTable) {
        return endpoint(`${PARENT}/${RESOURCE}`, 'services/table', {
            person: {
                name: theDeceased.name,
                gender: theDeceased.gender.toLowerCase(),
                date_of_birth: dateToServer(theDeceased.dateOfBirth),
                date_of_death: dateToServer(theDeceased.dateOfDeath),
            },
            claim: {
                claimant_name: claimantName,
                assumed_settlement: dateToServer(issueOfProceedings),
            },
            discount_rate: discountRate,
            discount_table: discountTable,
            multipliers: services.map(service => ({
                discount_table: service.discountTable,
                value: service.multiplicand || 0,
                period_of_dependency: parseFloat(service.duration),
            })),
        });
    },
    /**
     * Interest Calculation Endpoint.
     *
     * @param  {} preLitorLit
     * @param  {} assumedSettlement
     * @param  {} serviceOfProceedings
     * @param  {} dateOfDeath
     * @param  {} deductibles
     * @param  {} headsOfLoss
     * @param  {} interim
     * @param  {} pastLossOfEarningsTotal
     *
     * @return {Promise}
     */
    interest(preLitOrLit, assumedSettlement, serviceOfProceedings, onsetOfSymptoms, lifeExpectancy, deductibles, headsOfLoss, interim) {

        let interest = {
            pastDependencyOnIncomeInterest: headsOfLoss.pastDependencyOnIncomeInterest,
            pastDependencyOnServicesInterest: headsOfLoss.pastDependencyOnServicesInterest,
            generalDamagesInterest: headsOfLoss.generalDamagesInterest,
            bereavementAwardInterest: headsOfLoss.bereavementAwardInterest,
            funeralExpensesInterest: headsOfLoss.funeralExpensesInterest,
            lossOfSpouseInterest: headsOfLoss.lossOfSpouseInterest,
            pastCostOfCareInterest: headsOfLoss.pastCostOfCareInterest,
            futureCostOfCareInterest: headsOfLoss.futureCostOfCareInterest,
            pastCostOfTravelInterest: headsOfLoss.pastCostOfTravelInterest,
            pastLossOfEarningsInterest: headsOfLoss.pastLossOfEarningsInterest,
            miscInterest: headsOfLoss.miscInterest,
            pastCostOfServicesInterest: headsOfLoss.pastCostOfServicesInterest,
            pastReducedEarningsInterest: headsOfLoss.pastReducedEarningsInterest,
        };

        return endpoint(`${PARENT}/${RESOURCE}`, 'interest', {
            claim: {
                assumed_settlement: dateToServer(assumedSettlement),
                service_of_proceedings: dateToServer(serviceOfProceedings),
                type: preLitOrLit
            },
            deceased: {
                onset_of_symptoms: dateToServer(onsetOfSymptoms),
            },
            deductibles,
            heads_of_loss: {
                // Don't apply general damages to preLit claims
                generalDamages: headsOfLoss.generalDamages,
                pastDependencyOnIncome: headsOfLoss.pastDependencyOnIncome,
                pastDependencyOnServices: headsOfLoss.pastDependencyOnServices,
                bereavementAward: headsOfLoss.bereavementAward,
                funeralExpenses: headsOfLoss.funeralExpenses,
                lossOfSpouse: headsOfLoss.lossOfSpouse,
                pastLossOfEarnings: headsOfLoss.pastLossOfEarnings,
                pastCostOfCare: headsOfLoss.pastCostOfCare,
                futureCostOfCare: headsOfLoss.futureCostOfCare,
                pastCostOfTravel: headsOfLoss.pastCostOfTravel,
                misc: headsOfLoss.misc,
                pastCostOfServices: headsOfLoss.pastCostOfServices,
                pastReducedEarnings: headsOfLoss.pastReducedEarnings,
            },
            interest,
            interestPeriod: {
                type: headsOfLoss.interestType,
                value: headsOfLoss.customInterestDate,
            },
            interimPayments: interim,
            custom_heads_of_loss: headsOfLoss.customHeadsOfLoss ? headsOfLoss.customHeadsOfLoss : headsOfLoss.headsOfLoss
        });
    },
    /**
     * Custom Heads of Loss Interest Calculation
     *
     * @param  {} assumedSettlement
     * @param  {} onsetOfSymptoms
     * @param  {} assumedDateOfDeath
     * @param  {} customHeadsOfLoss
     *
     * @return {Promise}
     */
    customInterest(assumedSettlement, onsetOfSymptoms, assumedDateOfDeath, customHeadsOfLoss) {
        return endpoint(`${PARENT}/${RESOURCE}`, 'custom-interest', {
            claim: {
                assumed_settlement: dateToServer(assumedSettlement),
            },
            deceased: {
                date_of_death: dateToServer(assumedDateOfDeath),
                onset_of_symptoms: dateToServer(onsetOfSymptoms),
            },
            custom: customHeadsOfLoss.headsOfLoss ? customHeadsOfLoss.headsOfLoss : customHeadsOfLoss.customHeadsOfLoss,
        });
    },
    /**
     * Detailed Care Endpoint.
     *
     * @param  {} care
     *
     * @return {Promise}
     */
    detailedCare(care) {
        return endpoint(`${PARENT}/${RESOURCE}`, 'care/detailed', {
            care
        });
    },
    /**
     * Autocalculated Care Ednpoint.
     *
     * @return {Promise}
     */
    autoCalculateCare(gratuitousCare) {
        return endpoint(`${PARENT}/${RESOURCE}`, 'care/auto-calculate', {});
    },
    /**
     * Future calculated Care Endpoint.
     *
     * @return {Promise}
     */
    otherFutureLosses(period) {
        return endpoint(`${PARENT}/${RESOURCE}`, 'calculateFutureLosses', {
            multiplicand: period.multiplicand,
            start_date: period.startDate,
            gender: period.gender,
            discount_rate: period.discountRate,
            end_date: period.endDate,
            mortality_multiplier: period.mortalityMultiplier,
            date_of_birth: period.dateOfBirth,
            assumed_settlement: period.assumedSettlement,
        });
    },
    /**
     * Loss of Income Tables for Lost Years
     * @param  {} claimant
     * @param  {} assumedSettlement
     * @param  {} discountRate
     *
     * @return {Promise}
     */
    lossOfIncomeTables(claimant, assumedSettlement, discountRate) {
        const action = 'lost-years/loss-of-income';

        claimant.dateOfDeath = claimant.lifeExpectancy.dateOfDeath;
        claimant.dateOfDeathAssumed = claimant.assumedLifeExpectancy.dateOfDeath;

        return endpoint(`${PARENT}/${RESOURCE}`, action, {
            claimant: claimant,
            claim: {
                claimant_name: claimant.name,
                assumed_settlement: dateToServer(assumedSettlement),
            },
            discount_rate: discountRate,
            loss_of_earnings: claimant.income.multiplier
        });
    },
    /**
     * Post Injury Loss of Income Tables for Personal Injury
     * @param  {} claimant
     * @param  {} assumedSettlement
     * @param  {} discountRate
     *
     * @return {Promise}
     */
    postInjuryLossOfIncomeTables(claimant, claimInfo) {
        const action = 'personal-injury/loss-of-income';

        return endpoint(`${PARENT}/${RESOURCE}`, action, {
            claimant: claimant,
            claim: {
                claimant_name: claimant.name,
                assumed_settlement: dateToServer(claimInfo.dateOfAssumedSettlement),
            },
            claim_info: {
                discount_rate: claimInfo.discountRate,
                procedural_status: claimInfo.proceduralStatus,
                date_of_injury: claimInfo.dateOfInjury,
                date_of_assumed_settlement: claimInfo.dateOfAssumedSettlement,
                level_of_education: claimant.levelOfEducation,
                disabled_at_accident: claimant.disabledAtAccident,
                employed_at_accident: claimant.employedAtAccident,
                disabled_at_trial: claimant.disabledAtTrial,
                employed_at_trial: claimant.employedAtTrial,
            },
        });
    },
    /**
     * Loss of Earnings - contingency multiplier endpoint
     *
     * @return {Promise}
     */
    employmentContingencyDiscount(payload) {
        return endpoint(`${PARENT}/${RESOURCE}`, 'employment-contingency-discount', payload);
    },

    /**
     * Past Loss of Earnings.
     *
     * @return {Promise}
     */
    pastLossOfEarnings(payload) {
        return endpoint(`${PARENT}/${RESOURCE}`, 'past-loss-of-earnings', payload);
    },

    /**
     * Mini calc Income Endpoint.
     *
     * @param  {} income
     * @param  {} deceased
     * @param  {} dependant
     *
     * @return {object}
     */
    miniIncome(income) {
        return endpoint(`${PARENT}/${RESOURCE}`, 'income', {
            employment_income: income.employmentIncome,
            employment_net_gross: income.employmentNetGross,
            employment_type: income.employmentType,
            end_of_period: income.endOfPeriod,
            pension_income: income.pensionIncome,
            pension_net_gross: income.pensionNetGross,
            start_of_period: income.startOfPeriod,
            retirement_date: income.retirementDate,
        }).then((data) => {
            return {
                totalGrossIncome: data.total_gross_income,
                totalNetIncome: data.total_net_income,
            };
        });
    },
    /**
     * Post Injury Income Endpoint.
     *
     * @param  {} income
     * @param  {} person
     * @param  {} dateOfInjury
     *
     * @return {object}
     */
    postInjuryIncome(income, person, dateOfInjury) {
        return endpoint(`${PARENT}/${RESOURCE}`, 'postInjuryIncome', {
            pastPeriod: {
                end_of_period: dateOfInjury,
                employment_income: income.employmentIncome,
                employment_type: income.employmentType,
            },
            retirement_date: person.income.retirementDate,
            employment_income: income.employmentIncome,
            employment_type: income.employmentType,
            end_of_period: income.endOfPeriod,
            start_of_period: income.startOfPeriod,
            employment_net_gross: income.employmentNetGross,
        }).then((data) => {
            return {
                periodLoss: data.period_loss,
                totalNetIncome: data.total_net_income
            };
        });

        return endpoint(`${PARENT}/${RESOURCE}`, 'income', {
            employment_income: income.employmentIncome,
            employment_net_gross: income.employmentNetGross,
            employment_type: income.employmentType,
            end_of_period: income.endOfPeriod,
            pension_income: income.pensionIncome,
            pension_net_gross: income.pensionNetGross,
            start_of_period: income.startOfPeriod,
            retirement_date: person.income.retirementDate,
        }).then((data) => {
            return {
                totalGrossIncome: data.total_gross_income,
                totalNetIncome: data.total_net_income,
            };
        });
    },
    /**
     * Future Loss Multiplier Endpoint.
     *
     * @return {object}
     */
    futureLossMultiplier(payload) {
        return endpoint(`${PARENT}/${RESOURCE}`, 'multipliers/future-loss', payload);
    },
    /**
     * Future Purchases Endpoint.
     *
     * @return {Promise}
     */
    futurePurchase(purchase) {
        return endpoint(`${PARENT}/${RESOURCE}`, 'purchase/future-purchase', {
            multiplicand: purchase.multiplicand,
            purchase_date: purchase.dateOfPurchase,
            discount_rate: purchase.discountRate,
            date_of_birth: purchase.dateOfBirth,
            assumed_settlement: purchase.assumedSettlement,
            resale_value: purchase.resaleValue,
            type: purchase.type,
            purchase_end_date: purchase.purchaseEnd,
            frequency: purchase.frequency,
            gender: purchase.gender,
            mortality_multiplier: purchase.mortalityMultiplier,
        });
    },
};



// WEBPACK FOOTER //
// ./src/services/api/calculators/monetary.js